import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../../../components/header/header";
import Footer from "../../../components/footer/footer";
import { getPostBySlug, getCommentsByPostId, submitWpComment, getRelatedPosts, getCategories } from "../../../utilities/wp";
import "./blog.css";
import { useTranslation } from "react-i18next";

const BlogPost = () => {
    const { slug } = useParams();

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const BLOG_BANNER = `${process.env.PUBLIC_URL}/img/banner-blog.jpg`;
    const { t } = useTranslation();
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [errorComments, setErrorComments] = useState("");
    const [commentForm, setCommentForm] = useState({
        name: "",
        email: "",
        website: "",
        comment: "",
    });
    const [sendingComment, setSendingComment] = useState(false);
    const [commentMsg, setCommentMsg] = useState("");
    const [commentErr, setCommentErr] = useState("");
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [errorCategories, setErrorCategories] = useState("");



    useEffect(() => {
        let mounted = true;

        (async () => {
        setLoading(true);
        setError("");

        try {
            const data = await getPostBySlug(slug); // ✅ trae 1 post por slug
            if (mounted) setPost(data);
        } catch (e) {
            if (mounted) setError(e?.message ||  t("BLOG_POST_ERROR"));
        } finally {
            if (mounted) setLoading(false);
        }
        })();

        return () => {
        mounted = false;
        };
        }, [slug]);

    useEffect(() => {
        if (!post?.id) return;

            let mounted = true;

            (async () => {
                setLoadingComments(true);
                setErrorComments("");

                try {
                const data = await getCommentsByPostId(post.id, { perPage: 50 });
                if (mounted) setComments(data);
                } catch (e) {
                if (mounted) setErrorComments(e?.message || t("BLOG_COMMENTS_ERROR"));
                } finally {
                if (mounted) setLoadingComments(false);
                }
        })();

        return () => {
            mounted = false;
        };
    }, [post?.id]);

    async function handleSubmitComment(e) {
        e.preventDefault();
        if (!post?.id) return;

        setCommentErr("");
        setCommentMsg("");

        const name = commentForm.name.trim();
        const email = commentForm.email.trim();
        const comment = commentForm.comment.trim();

        if (!name || !email || !comment) {
            setCommentErr("Nombre, email y comentario son obligatorios.");
            return;
        }

        setSendingComment(true);

        try {
            await submitWpComment({
            postId: post.id,
            authorName: name,
            authorEmail: email,
            authorUrl: commentForm.website,
            content: comment,
            });

            setCommentMsg(t("BLOG_FORM_SUBMIT_SUCCESS"));
            setCommentForm({ name: "", email: "", website: "", comment: "" });

            // Recargar lista (si se aprueba automático o para ver si aparece)
            const data = await getCommentsByPostId(post.id, { perPage: 50 });
            setComments(data);
        } catch (err) {
            setCommentErr(err?.message || t("BLOG_FORM_SUBMIT_ERROR"));
        } finally {
            setSendingComment(false);
        }
    }

    useEffect(() => {
        if (!post?.id || !post?.categories?.length) return;

        let mounted = true;

        (async () => {
            try {
            const data = await getRelatedPosts({
                categoryIds: post.categories,
                excludeId: post.id,
                perPage: 3
            });

            if (mounted) setRelatedPosts(data);
            } catch (err) {
            console.warn("No se pudieron cargar relacionados", err);
            }
        })();

        return () => (mounted = false);
    }, [post]);

    useEffect(() => {
        let mounted = true;

        (async () => {
            setLoadingCategories(true);
            setErrorCategories("");

            try {
                const cats = await getCategories();
                if (mounted) setCategories(cats);
            } catch (e) {
                if (mounted) setErrorCategories("Error cargando categorías");
            } finally {
                if (mounted) setLoadingCategories(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, []);



    return (
    <>
    <Header />

        <div className="blog-page">
            <Link to="/blog" className="blogpost-backlink">← {t("BLOG_BACKTO_BLOG")}</Link>

            {loading && (
                <div className="blog-container">
                    <div className="blog-loader" role="status" aria-live="polite">
                    <span className="blog-spinner" aria-hidden="true" />
                    <span className="blog-loader-text">{t("BLOG_LOADING")}</span>
                    </div>
                </div>
            )}


            {!loading && error && (
                <div className="blog-container">
                <div className="blog-error">
                    <p className="blog-error-title">Ups 😵‍💫</p>
                    <p className="blog-error-text">{error}</p>
                    <Link to="/blog" className="blog-retry-link">{t("BLOG_BACKTO_BLOG")}</Link>
                </div>
                </div>
            )}

            {!loading && !error && post && (
                <>
                    {/*  Banner FULL WIDTH */}
                    <div
                        className="blogpost-banner"
                        style={{ backgroundImage: `url(${BLOG_BANNER})` }}
                    >
                        <div className="blogpost-banner-overlay">
                        <h1 className="blogpost-banner-title">{post.title}</h1>
                        </div>
                    </div>

                    {/* Contenido + comentarios centrados */}
                    <div className="blog-container blogpost-layout">
                        {/* Columna principal */}
                        <main className="blogpost-main">
                            <article className="blog-post">
                            {post.image && (
                                <div className="blogpost-featured">
                                <img src={post.image} alt={post.title} loading="lazy" />
                                </div>
                            )}

                            <div
                                className="blog-post-content"
                                dangerouslySetInnerHTML={{ __html: post.content }}
                            />
                            </article>

                            {/* Comentarios dentro del container */}
                            <section className="wp-comments">
                                <h2 className="wp-comments-title">
                                    {comments.length} {comments.length === 1 ? t("BLOG_FORM_COMMENT") : t("BLOG_FORM_COMMENT_2")}
                                </h2>

                                {loadingComments && <p className="wp-comments-loading">{t("BLOG_LOADING_COMMENTS")}</p>}

                                {!loadingComments && errorComments && (
                                    <p className="wp-comments-error">{errorComments}</p>
                                )}

                                {!loadingComments && !errorComments && comments.length === 0 && (
                                    <p className="wp-comments-empty">{t("BLOG_COMMENTS_EMPTY")}</p>
                                )}

                                {!loadingComments && !errorComments && comments.length > 0 && (
                                    <ul className="wp-comments-list">
                                    {comments.map((c) => (
                                        <li key={c.id} className="wp-comment">
                                        <div className="wp-comment-head">
                                            <strong className="wp-comment-author">{c.authorName}</strong>
                                            <span className="wp-comment-date">
                                            {new Date(c.date).toLocaleDateString("es-CO")}
                                            </span>
                                        </div>

                                        <div
                                            className="wp-comment-body"
                                            dangerouslySetInnerHTML={{ __html: c.content }}
                                        />
                                        </li>
                                    ))}
                                    </ul>
                                )}

                                <div className="wp-comment-form-wrap">
                                    <h3 className="wp-comment-form-title">{t("BLOG_LEAVE_COMMENT")}</h3>

                                    {commentMsg && <p className="wp-comment-success">{commentMsg}</p>}
                                    {commentErr && <p className="wp-comment-error">{commentErr}</p>}

                                    <form className="wp-comment-form" onSubmit={handleSubmitComment}>
                                        <div className="wp-comment-grid">
                                        <div className="field">
                                            <label>{t("BLOG_FORM_NAME")}</label>
                                            <input
                                                value={commentForm.name}
                                                onChange={(e) => setCommentForm((s) => ({ ...s, name: e.target.value }))}
                                                placeholder={t("BLOG_FORM_NAME_PH")}
                                                required
                                            />
                                        </div>

                                        <div className="field">
                                            <label>{t("BLOG_FORM_EMAIL")}</label>
                                            <input
                                                value={commentForm.email}
                                                onChange={(e) => setCommentForm((s) => ({ ...s, email: e.target.value }))}
                                                placeholder={t("BLOG_FORM_EMAIL_PH")}
                                                required
                                            />
                                        </div>

                                        <div className="field field-full">
                                            <label>{t("BLOG_FORM_WEBSITE")}</label>
                                            <input
                                                value={commentForm.website}
                                                onChange={(e) => setCommentForm((s) => ({ ...s, website: e.target.value }))}
                                                placeholder="https://"
                                            />
                                        </div>

                                        <div className="field field-full">
                                            <label>{t("BLOG_FORM_COMMENT")}</label>
                                            <textarea
                                                rows={5}
                                                value={commentForm.comment}
                                                onChange={(e) => setCommentForm((s) => ({ ...s, comment: e.target.value }))}
                                                placeholder={t("BLOG_FORM_COMMENT_PH")}
                                                required
                                            />
                                        </div>
                                        </div>

                                        <button className="wp-comment-submit" type="submit" disabled={sendingComment}>
                                        {sendingComment ? t("BLOG_FORM_SENDING") : t("BLOG_FORM_SUBMIT")}
                                        </button>
                                    </form>
                                </div>

                            </section>
                        </main>

                        {/* Sidebar */}
                        <aside className="blogpost-aside">
                            <div className="blogpost-aside-card">
                                <h3 className="blogpost-aside-title">{t("BLOG_RELATED_TITLE")}</h3>

                                 <div className="blog-related-list">
                                    {relatedPosts.map((rp) => (
                                        <Link key={rp.id} to={`/blog/${rp.slug}`} className="blog-related-item">
                                        {rp.image && (
                                            <img src={rp.image} alt={rp.title} />
                                        )}
                                        <span>{rp.title}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            <div className="blogpost-aside-card">
                                <h3 className="blogpost-aside-title">{t("BLOG_CATEGORIES_TITLE")}</h3>

                                {loadingCategories && (
                                    <p className="blogpost-aside-muted">
                                        {t("BLOG_CATEGORIES_LOADING")}
                                    </p>
                                )}

                                {!loadingCategories && errorCategories && (
                                    <p className="blogpost-aside-muted">
                                        {errorCategories}
                                    </p>
                                )}

                                {!loadingCategories && !errorCategories && categories.length > 0 && (
                                    <ul className="blog-categories-list">
                                        {categories.map((cat) => (
                                            <li key={cat.id}>
                                                <Link
                                                    to={`/blog?cat=${cat.id}`}
                                                    className="blog-category-item"
                                                >
                                                    <span>{cat.name}</span>
                                                    <span className="blog-category-count">
                                                        {cat.count}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                            </div>
                        </aside>
                    </div>
                </>
            )}
            </div>

            <Footer />
        </>
    );

};

export default BlogPost;